<template>
  <div>
    <Transition
      name="expand"
      :class="speed"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <template v-if="lazyRendering">
        <div v-if="!collapsed">
          <slot />
        </div>
      </template>
      <template v-else>
        <div v-show="!collapsed">
          <slot />
        </div>
      </template>
    </Transition>
  </div>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    collapsed?: boolean;
    speed?: string;
    lazyRendering?: boolean;
  }>(),
  {
    collapsed: true,
    speed: 'slow',
    lazyRendering: false,
  },
);

const prepareElement = (element: HTMLElement): void => {
  const { width } = getComputedStyle(element);
  const newStyle = {
    width,
    position: 'absolute',
    visibility: 'hidden',
    height: 'auto',
  };
  Object.assign(element.style, newStyle);
};

const resetStyle = (element: HTMLElement): void => {
  const newStyle = {
    position: null,
    visibility: null,
    width: null,
    height: '0',
  };
  Object.assign(element.style, newStyle);
};

const forceRepaint = (element: HTMLElement): void => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  getComputedStyle(element).height;
};

const animateHeight = (element: HTMLElement, height: string): void => {
  requestAnimationFrame(() => {
    const newStyle = {
      height,
    };
    Object.assign(element.style, newStyle);
  });
};

const enter = (element: Element): void => {
  const el = element as HTMLElement;
  prepareElement(el);
  const { height } = getComputedStyle(el);
  resetStyle(el);
  forceRepaint(el);
  animateHeight(el, height);
};

const afterEnter = (element: Element): void => {
  const el = element as HTMLElement;
  const newStyle = {
    height: 'auto',
  };
  Object.assign(el.style, newStyle);
};

const leave = (element: Element): void => {
  const el = element as HTMLElement;
  const { height } = getComputedStyle(el);
  const newStyle = { height };
  Object.assign(el.style, newStyle);
  forceRepaint(el);
  animateHeight(el, '0');
};
</script>

<style scoped>
* {
  will-change: height;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.expand-enter-active.slow,
.expand-leave-active.slow {
  transition: height 0.4s cubic-bezier(.8,0,.2,1);
  overflow: hidden;
}

.expand-enter-active.fast,
.expand-leave-active.fast {
  transition: height 0.15s cubic-bezier(.8,0,.2,1);
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
}
</style>
